import type { TFunction } from "i18next";
import { Link } from "react-router-dom";
import { ChevronRight, Truck } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDisplayDate } from "@/lib/dateFormat";
import { formatRecibidaTotal } from "@/pages/invoices/recibidas-list-shared";
import { RecibidaAckCfeCard } from "@/pages/invoices/RecibidaAckCfeCard";
import type { RecibidaDetail } from "@/services/invoiceService";

const formatTotal = formatRecibidaTotal;

const pageCardClass =
  "shadow-[0_1px_2px_rgba(0,0,0,0.04)] dark:shadow-sm";

export interface RecibidaDetailViewProps {
  detail: RecibidaDetail;
  t: TFunction;
  i18nLanguage: string;
}

export function RecibidaDetailView({
  detail,
  t,
  i18nLanguage,
}: RecibidaDetailViewProps): JSX.Element {
  const moneda = detail.moneda || "UYU";
  const payload = detail.payload;
  const items = (payload?.items ?? []) as Array<{
    nomItem?: string;
    dscItem?: string;
    cantidad?: number;
    precioUnitario?: number;
    montoItem?: number;
  }>;
  const referencias = payload?.referencias ?? [];

  const issuerName = detail.legalName?.trim() || detail.rucEmisor || "—";
  const typeSerie =
    detail.tipoCFE != null
      ? `${t(`invoices.cfeTypes.${detail.tipoCFE}`, detail.tipoCFE)} ${detail.serie ?? ""}`.trim()
      : "—";
  const paymentLabel =
    detail.formaPago === "credito"
      ? t("invoices.list.paymentMethodCredito", "Credit")
      : detail.formaPago === "contado"
        ? t("invoices.list.paymentMethodContado", "Spot")
        : "—";

  const supplierId = (detail as RecibidaDetail & { supplierId?: string | null })
    .supplierId;

  return (
    <div className="space-y-4">
      {supplierId ? (
        <Link
          to={`/suppliers/${supplierId}`}
          className={`group flex items-center gap-3 rounded-lg border border-primary/15 bg-card px-4 py-3 transition-colors hover:border-primary/30 hover:bg-primary/[0.03] ${pageCardClass}`}
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <Truck className="h-4 w-4 text-primary" aria-hidden />
          </div>
          <div className="min-w-0 flex-1 text-sm">
            <div className="truncate font-semibold text-foreground">
              {t("recibidas.supplierBannerTitle", "Proveedor: {{name}}", {
                name: issuerName,
              })}
            </div>
            <div className="text-xs text-muted-foreground">
              {t(
                "recibidas.supplierBannerHint",
                "Ver o configurar la ficha de este proveedor (aceptación automática, categoría por defecto y más).",
              )}
            </div>
          </div>
          <ChevronRight
            className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
            aria-hidden
          />
        </Link>
      ) : null}
      <Card className={pageCardClass}>
        <CardHeader>
          <CardTitle className="text-foreground">
            {t("invoices.detail.invoiceDetails", "Invoice details")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <span className="text-muted-foreground">
              {t("recibidas.issuer", "Issuer")}:
            </span>{" "}
            <span className="font-medium text-foreground">{issuerName}</span>
          </p>
          {detail.rucEmisor ? (
            <p>
              <span className="text-muted-foreground">
                {t("recibidas.taxId", "Tax ID (RUT)")}:
              </span>{" "}
              <span className="tabular-nums text-foreground">{detail.rucEmisor}</span>
            </p>
          ) : null}
          <p>
            <span className="text-muted-foreground">
              {t("recibidas.date", "Date")}:
            </span>{" "}
            {detail.fechaHora
              ? formatDisplayDate(detail.fechaHora.slice(0, 10), {
                  locale: i18nLanguage,
                })
              : "—"}
          </p>
          <p>
            <span className="text-muted-foreground">
              {t("recibidas.typeSerie", "Type / Series")}:
            </span>{" "}
            {typeSerie}
          </p>
          <p>
            <span className="text-muted-foreground">
              {t("recibidas.paymentMethod", "Payment method")}:
            </span>{" "}
            {paymentLabel}
          </p>
          <p>
            <span className="text-muted-foreground">
              {t("recibidas.number", "Number")}:
            </span>{" "}
            <span className="font-medium text-foreground">{detail.nro ?? "—"}</span>
          </p>
          <p>
            <span className="text-muted-foreground">
              {t("invoices.detail.currency", "Currency")}:
            </span>{" "}
            {moneda}
          </p>
          <p>
            <span className="text-muted-foreground">
              {t("invoices.detail.total", "Total")}:
            </span>{" "}
            <span className="font-semibold text-foreground">
              {formatTotal(moneda, detail.montoTotal ?? 0)}
            </span>
          </p>
        </CardContent>
      </Card>

      <Card className={pageCardClass}>
        <CardHeader>
          <CardTitle className="text-foreground">
            {t("invoices.detail.lineItems", "Line items")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {items.length > 0 ? (
            <>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {t("recibidas.itemDescription", "Description")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("recibidas.qty", "Qty")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("recibidas.unitPrice", "Unit price")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("recibidas.amount", "Amount")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, idx) => (
                      <TableRow key={idx} className="even:bg-muted/30">
                        <TableCell>
                          <div>{item.nomItem ?? "—"}</div>
                          {item.dscItem && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {item.dscItem}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {item.cantidad ?? "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {item.precioUnitario != null
                            ? formatTotal(moneda, item.precioUnitario)
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {item.montoItem != null
                            ? formatTotal(moneda, item.montoItem)
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <ul className="space-y-3 md:hidden">
                {items.map((item, idx) => (
                  <li
                    key={idx}
                    className="rounded-md border border-border bg-background/40 p-3"
                  >
                    <p className="text-sm font-medium text-foreground">
                      {item.nomItem ?? "—"}
                    </p>
                    {item.dscItem && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {item.dscItem}
                      </p>
                    )}
                    <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          {t("recibidas.qty", "Qty")}
                        </dt>
                        <dd className="tabular-nums text-foreground">
                          {item.cantidad ?? "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          {t("recibidas.unitPrice", "Unit price")}
                        </dt>
                        <dd className="tabular-nums text-foreground">
                          {item.precioUnitario != null
                            ? formatTotal(moneda, item.precioUnitario)
                            : "—"}
                        </dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-xs text-muted-foreground">
                          {t("recibidas.amount", "Amount")}
                        </dt>
                        <dd className="font-semibold tabular-nums text-foreground">
                          {item.montoItem != null
                            ? formatTotal(moneda, item.montoItem)
                            : "—"}
                        </dd>
                      </div>
                    </dl>
                  </li>
                ))}
              </ul>
            </>
          ) : detail.hasRawXml === false ? (
            /*
             * No XML, so there are no items to show — and that is the
             * emisor's doing, not ours. DGI's recibidos consulta returns
             * headers and totals only; the líneas exist solely inside the CFE
             * XML, which reaches us when the emisor delivers the sobre to our
             * reception channel. Saying "sin ítems" flatly had accountants
             * (correctly) suspecting Fiscalo of losing the detail.
             */
            <p className="text-sm text-muted-foreground">
              {t(
                "recibidas.lineItemsNotSentByIssuer",
                "El emisor no envió el detalle de este comprobante. DGI informa únicamente los totales, que son los que ves acá — pedíselo al proveedor si necesitás el detalle por ítem.",
              )}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t("invoices.detail.lineItemsEmpty", "No line items.")}
            </p>
          )}
          <div
            className={
              items.length > 0
                ? "mt-4 flex flex-col items-end gap-1 text-sm"
                : "mt-2 flex flex-col items-end gap-1 text-sm"
            }
          >
            <p className="text-muted-foreground">
              {t("recibidas.net", "Net")}:{" "}
              <span className="tabular-nums text-foreground">
                {formatTotal(moneda, detail.totalNeto ?? 0)}
              </span>
            </p>
            <p className="text-muted-foreground">
              {t("recibidas.iva", "VAT")}:{" "}
              <span className="tabular-nums text-foreground">
                {formatTotal(moneda, detail.totalIVA ?? 0)}
              </span>
            </p>
            <p className="font-semibold text-foreground">
              {t("recibidas.total", "Total")}:{" "}
              <span className="tabular-nums">
                {formatTotal(moneda, detail.montoTotal ?? 0)}
              </span>
            </p>
            {(detail.totalRetenido ?? 0) !== 0 ? (
              <p className="text-muted-foreground">
                {t("recibidas.retenido", "Withheld")}:{" "}
                <span className="tabular-nums text-foreground">
                  {formatTotal(moneda, detail.totalRetenido ?? 0)}
                </span>
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {referencias.length > 0 ? (
        <Card className={pageCardClass}>
          <CardHeader>
            <CardTitle className="text-foreground">
              {t("recibidas.references", "References")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
              {referencias.map((ref, idx) => (
                <li key={idx}>
                  {ref.tpoDocRef ?? "Doc"} {ref.serie ?? ""} {ref.nroCFERef ?? ""}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {/* Receptor's business-layer accept/reject (ACKCFE, DGI §XVIII) —
          rendered above the ACKSobre status because it's the decision
          users need to make; the transport ack is just informational. */}
      <RecibidaAckCfeCard detail={detail} t={t} i18nLanguage={i18nLanguage} />

      <Card className={pageCardClass}>
        <CardHeader>
          <CardTitle className="text-muted-foreground text-sm font-medium">
            {t("recibidas.acuseReciboTitle", "Acuse de recibo")}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {detail.acuseSentAt ? (
            <StatusBadge variant="success">
              {t("recibidas.acuseReciboSent", "Enviado {{date}}", {
                date: formatDisplayDate(detail.acuseSentAt, { locale: i18nLanguage }),
              })}
            </StatusBadge>
          ) : !detail.envioCfeMessageId ? (
            <span className="text-muted-foreground">
              {t(
                "recibidas.acuseReciboNotApplicable",
                "No corresponde — esta factura llegó por sincronización con DGI o carga manual, no por correo del emisor, así que no hay sobre que acusar.",
              )}
            </span>
          ) : detail.acuseSkippedReason === "no_emisor_email" ? (
            <span className="text-muted-foreground">
              {t(
                "recibidas.acuseReciboNoEmail",
                "El emisor no tiene correo de notificación registrado en DGI.",
              )}
            </span>
          ) : detail.acuseSkippedReason ? (
            <span className="text-muted-foreground">
              {t(
                "recibidas.acuseReciboSkippedGeneric",
                "Acuse no enviable: {{reason}}",
                { reason: detail.acuseSkippedReason },
              )}
            </span>
          ) : (
            <StatusBadge variant="warning">
              {t("recibidas.acuseReciboPending", "Pendiente")}
            </StatusBadge>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
